import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, Progress } from '../components/ui';
import { GenerationQueue } from '../components/generation';
import {
  BusinessInfoForm,
  LocationForm,
  ContactInfoForm,
  WebsiteConfigForm,
  DesignConfigForm,
  ReviewForm,
} from '../components/forms';
import { useForm } from '../hooks/useForm';
import { useGeneration } from '../contexts/GenerationContext';
import { useAuth } from '../contexts/AuthContext';
import type {
  BusinessInfo,
  LocationInfo,
  ContactInfo,
  WebsiteConfig,
  DesignConfig,
  FormStep,
} from '../types';

/**
 * Section renderers keyed by section id. The wizard step may include one
 * or more of these; they stack vertically inside the form card with a
 * dashed subheader between them.
 */
type SectionRendererProps = {
  sectionId: FormStep;
  formData: ReturnType<typeof useForm>['formData'];
  errors: Record<string, string>;
  updaters: {
    business: (data: Partial<BusinessInfo>) => void;
    location: (data: Partial<LocationInfo>) => void;
    contact: (data: Partial<ContactInfo>) => void;
    website: (data: Partial<WebsiteConfig>) => void;
    design: (data: Partial<DesignConfig>) => void;
  };
  goToSection: (s: FormStep) => void;
};

function SectionRenderer({ sectionId, formData, errors, updaters, goToSection }: SectionRendererProps) {
  switch (sectionId) {
    case 'business':
      return (
        <BusinessInfoForm
          data={formData.business}
          onChange={updaters.business}
          errors={errors}
        />
      );
    case 'location':
      return (
        <LocationForm
          data={formData.location}
          onChange={updaters.location}
          errors={errors}
        />
      );
    case 'contact':
      return (
        <ContactInfoForm
          data={formData.contact}
          onChange={updaters.contact}
          errors={errors}
          country={formData.location.country}
          city={formData.location.city}
          domain={formData.website.domain}
        />
      );
    case 'website':
      return (
        <WebsiteConfigForm
          data={formData.website}
          onChange={updaters.website}
          errors={errors}
        />
      );
    case 'design':
      return (
        <DesignConfigForm
          data={formData.design}
          onChange={updaters.design}
          errors={errors}
        />
      );
    case 'review':
      return (
        <ReviewForm
          data={formData}
          onEdit={(section) => goToSection(section as FormStep)}
        />
      );
    default:
      return null;
  }
}

const SECTION_TITLES: Record<FormStep, string> = {
  business: 'Company profile',
  location: 'Registered address',
  website: 'Domain & configuration',
  contact: 'Contact endpoints',
  design: 'Design',
  review: 'Review',
};

/**
 * Editorial copy for each wizard step. Keyed by wizardStep.id. The h2 is
 * intentionally different from the eyebrow label so the two don't repeat
 * each other ("STEP 01 · BUSINESS & LOCATION" + "Business & Location.").
 */
const STEP_COPY: Record<string, { title: string; subtitle: string }> = {
  'business-location': {
    title: 'Tell us about the business.',
    subtitle:
      'Drafts the About, Legal, and Contact pages. Required unless marked optional.',
  },
  'website-contact': {
    title: 'Set the website shape.',
    subtitle:
      'Domain, language, region, and how customers reach you. All fields required.',
  },
  design: {
    title: 'Pick the look.',
    subtitle:
      'Colors, typography, and feel. You can change any answer before we generate.',
  },
  review: {
    title: 'Review and generate.',
    subtitle:
      'Double-check every answer. Once you generate, the credit hold becomes a charge.',
  },
};

/**
 * Per-step contextual tip for the summary-rail tip block. Matches the
 * reference design's pattern of a small helpful hint that changes as
 * the user moves through the wizard.
 */
const STEP_TIP: Record<string, { title: string; body: string }> = {
  'business-location': {
    title: 'Tip',
    body: 'Accurate address data improves the generated legal footer and compliance score. Use the regenerate buttons next to street and postal code if you want a quick sample.',
  },
  'website-contact': {
    title: 'Tip',
    body: 'Your domain does not need to be live yet — we bake it into the canonical URLs, sitemap, and JSON-LD so it works once you point DNS.',
  },
  design: {
    title: 'Tip',
    body: 'Style preset drives the whole palette. Small edits to the primary color keep the preset feel intact; big swings can fight it.',
  },
  review: {
    title: 'Heads up',
    body: 'Hitting Generate converts your credit hold into a charge. A ZIP link lands in about two minutes.',
  },
};

export const HomePage: React.FC = () => {
  const {
    formData,
    currentStep,
    currentWizardStep,
    wizardSteps,
    errors,
    isLastStep,
    isFirstStep,
    updateFormData,
    nextStep,
    prevStep,
    goToSection,
    clearSavedForm,
  } = useForm();

  const { generate, isGenerating, reset } = useGeneration();
  const { user } = useAuth();
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  // Clear activeJobId on mount so the form is usable regardless of
  // background generations.
  useEffect(() => {
    reset();
  }, [reset]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (isLastStep) {
      clearSavedForm();
      generate(formData);
    } else {
      nextStep();
    }
  }, [isLastStep, clearSavedForm, generate, formData, nextStep]);

  const onBusinessChange = useCallback(
    (data: Partial<BusinessInfo>) => updateFormData('business', data),
    [updateFormData]
  );
  const onLocationChange = useCallback(
    (data: Partial<LocationInfo>) => updateFormData('location', data),
    [updateFormData]
  );
  const onContactChange = useCallback(
    (data: Partial<ContactInfo>) => updateFormData('contact', data),
    [updateFormData]
  );
  const onWebsiteChange = useCallback(
    (data: Partial<WebsiteConfig>) => updateFormData('website', data),
    [updateFormData]
  );
  const onDesignChange = useCallback(
    (data: Partial<DesignConfig>) => updateFormData('design', data),
    [updateFormData]
  );

  const updaters = useMemo(
    () => ({
      business: onBusinessChange,
      location: onLocationChange,
      contact: onContactChange,
      website: onWebsiteChange,
      design: onDesignChange,
    }),
    [onBusinessChange, onLocationChange, onContactChange, onWebsiteChange, onDesignChange]
  );

  // Progress indicator steps — 4 in total, derived from WIZARD_STEPS.
  const progressSteps = useMemo(
    () => wizardSteps.map((s, i) => ({ id: s.id, label: s.label, num: String(i + 1).padStart(2, '0') })),
    [wizardSteps]
  );

  // Stable per-mount draft id for the header build-tag. Decorative only —
  // not persisted or sent to the server, just a visible "yes this draft
  // is a real thing" token that the editorial design calls for.
  const draftId = useMemo(
    () => `wb-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  // Live summary data for the right-rail.
  const summaryRows: Array<{ k: string; v: string; faint?: boolean }> = [
    { k: 'Company', v: formData.business.name || '—', faint: !formData.business.name },
    { k: 'Entity', v: formData.business.legalEntity || '—', faint: !formData.business.legalEntity },
    { k: 'Country', v: formData.location.country || '—', faint: !formData.location.country },
    { k: 'City', v: formData.location.city || '—', faint: !formData.location.city },
    { k: 'Domain', v: formData.website.domain || '—', faint: !formData.website.domain },
    { k: 'Language', v: formData.website.language || '—', faint: !formData.website.language },
    { k: 'Preset', v: formData.design.stylePreset || '—' },
  ];

  // Hide the summary rail on the review step (the review IS a summary).
  const showSummary = !isLastStep;

  return (
    <div className="fade-in" style={{ maxWidth: '1280px', margin: '0 auto' }}>
      {/* Page head */}
      <header style={{ marginBottom: 'var(--space-8)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          <span className="eyebrow">NEW · DRAFT</span>
          <span className="build-tag">run-id · {draftId}</span>
        </div>
        <h1 style={{ letterSpacing: '-0.035em', fontWeight: 600, margin: 0 }}>
          New website.
        </h1>
        <p
          style={{
            color: 'var(--ink-muted)',
            marginTop: 'var(--space-2)',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.01em',
            margin: 'var(--space-2) 0 0',
          }}
        >
          {wizardSteps.length} steps · ~3min · autosave on
        </p>
      </header>

      {/* 4-step Progress indicator */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <Progress steps={progressSteps} currentStep={currentStep} />
      </div>

      {/* Form card + summary rail */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showSummary ? 'minmax(0, 1fr) 340px' : '1fr',
          gap: 'var(--space-6)',
          alignItems: 'flex-start',
        }}
        className="wizard-grid"
      >
        <style>{`
          @media (max-width: 1050px) {
            .wizard-grid { grid-template-columns: 1fr !important; }
            .wizard-summary { display: none !important; }
          }
        `}</style>

        <section
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Step head */}
          <div
            style={{
              padding: 'var(--space-6)',
              borderBottom: '1px solid var(--line)',
              background: 'linear-gradient(180deg, var(--bg-elevated), var(--bg))',
              position: 'relative',
            }}
          >
            <span
              className="coord"
              style={{
                position: 'absolute',
                top: 'var(--space-4)',
                right: 'var(--space-4)',
                color: 'var(--ink-faint)',
              }}
            >
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>
                {String(currentStep + 1).padStart(2, '0')}
              </span>
              <span style={{ margin: '0 2px' }}>/</span>
              {String(wizardSteps.length).padStart(2, '0')}
            </span>
            <span className="eyebrow eyebrow-accent">
              STEP {String(currentStep + 1).padStart(2, '0')} · {currentWizardStep.label.toUpperCase()}
            </span>
            <h2
              style={{
                marginTop: 'var(--space-3)',
                marginBottom: 0,
                fontWeight: 600,
                letterSpacing: '-0.03em',
                fontSize: 'var(--text-2xl)',
              }}
            >
              {STEP_COPY[currentWizardStep.id]?.title ??
                (isLastStep ? 'Ready to generate.' : currentWizardStep.label + '.')}
            </h2>
            {STEP_COPY[currentWizardStep.id]?.subtitle && (
              <p
                style={{
                  color: 'var(--ink-muted)',
                  fontSize: 'var(--text-sm)',
                  lineHeight: 1.55,
                  maxWidth: '62ch',
                  margin: 'var(--space-2) 0 0',
                }}
              >
                {STEP_COPY[currentWizardStep.id]!.subtitle}
              </p>
            )}
            {isAdmin && isLastStep && (
              <div
                role="alert"
                style={{
                  marginTop: 'var(--space-4)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--warning-bg)',
                  border: '1px solid var(--warning)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--warning)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                Admin accounts cannot generate websites. Please use a Company Manager or Company User account.
              </div>
            )}
          </div>

          {/* Form body — renders all sections for the current wizard step */}
          <form onSubmit={handleSubmit}>
            <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
              {currentWizardStep.sections.length === 0 ? (
                // Review step — render the ReviewForm which provides its own layout
                <SectionRenderer
                  sectionId="review"
                  formData={formData}
                  errors={errors}
                  updaters={updaters}
                  goToSection={goToSection}
                />
              ) : (
                currentWizardStep.sections.map((sectionId) => (
                  <div
                    key={sectionId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-4)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 'var(--space-3)',
                        paddingBottom: 'var(--space-3)',
                        borderBottom: '1px dashed var(--line)',
                      }}
                    >
                      <h4
                        style={{
                          fontSize: 'var(--text-base)',
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                          margin: 0,
                          color: 'var(--ink)',
                        }}
                      >
                        {SECTION_TITLES[sectionId]}
                      </h4>
                      <span className="eyebrow">Required</span>
                    </div>
                    <SectionRenderer
                      sectionId={sectionId}
                      formData={formData}
                      errors={errors}
                      updaters={updaters}
                      goToSection={goToSection}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Footer — step counter + back/continue */}
            <div
              style={{
                padding: 'var(--space-5) var(--space-6)',
                background: 'var(--bg-sunk)',
                borderTop: '1px solid var(--line)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--ink-muted)',
                    letterSpacing: '0.05em',
                  }}
                >
                  Step {String(currentStep + 1).padStart(2, '0')} / {String(wizardSteps.length).padStart(2, '0')}
                </span>
                {!isLastStep && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.65rem',
                      color: 'var(--ink-faint)',
                      letterSpacing: '0.06em',
                    }}
                  >
                    <span className="kbd">⌘</span>
                    <span className="kbd">↵</span>
                    <span>to continue</span>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={prevStep}
                  disabled={isFirstStep || isGenerating}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isGenerating}
                  disabled={isGenerating || (isLastStep && isAdmin)}
                  title={isLastStep && isAdmin ? 'Admins cannot generate websites.' : ''}
                  style={
                    isLastStep && !isAdmin
                      ? {
                          background: 'var(--accent)',
                          color: 'white',
                          borderColor: 'var(--accent)',
                          boxShadow: '0 4px 12px rgba(200, 65, 26, 0.25)',
                        }
                      : undefined
                  }
                >
                  {isLastStep ? 'Generate Website' : 'Continue'}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </form>
        </section>

        {/* Summary rail — hidden on review step */}
        {showSummary && (
          <aside
            className="wizard-summary"
            style={{
              position: 'sticky',
              top: 'calc(60px + var(--space-4))',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}
          >
            <div
              className="crosshair"
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4) var(--space-5)',
              }}
            >
              <span className="crosshair-extra-a" />
              <span className="crosshair-extra-b" />
              <h4
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-muted)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                SUMMARY · DRAFT
              </h4>
              {summaryRows.map((r, idx) => (
                <div
                  key={r.k}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    padding: '6px 0',
                    fontSize: 'var(--text-sm)',
                    lineHeight: 1.3,
                    borderBottom: idx < summaryRows.length - 1 ? '1px dashed var(--line)' : 'none',
                  }}
                >
                  <span style={{ color: 'var(--ink-muted)' }}>{r.k}</span>
                  <span
                    style={{
                      color: r.faint ? 'var(--ink-faint)' : 'var(--ink)',
                      fontWeight: r.faint ? 400 : 500,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-xs)',
                      maxWidth: '60%',
                      textAlign: 'right',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.v}
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4) var(--space-5)',
              }}
            >
              <h4
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-muted)',
                  marginBottom: 'var(--space-3)',
                }}
              >
                BUILD · ESTIMATES
              </h4>
              {[
                { k: 'Pages', v: '7 – 10' },
                { k: 'Build time', v: '~2 – 6 min' },
                { k: 'Cost', v: 'Based on your plan' },
              ].map((r, idx, arr) => (
                <div
                  key={r.k}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                    fontSize: 'var(--text-sm)',
                    lineHeight: 1.3,
                    borderBottom: idx < arr.length - 1 ? '1px dashed var(--line)' : 'none',
                  }}
                >
                  <span style={{ color: 'var(--ink-muted)' }}>{r.k}</span>
                  <span style={{ color: 'var(--ink)', fontWeight: 500, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                    {r.v}
                  </span>
                </div>
              ))}
            </div>

            {STEP_TIP[currentWizardStep.id] && (
              <div
                style={{
                  background: 'var(--accent-soft)',
                  border: '1px solid rgba(221, 77, 30, 0.18)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--accent-ink)',
                  lineHeight: 1.55,
                }}
              >
                <strong
                  style={{
                    display: 'block',
                    fontSize: 'var(--text-sm)',
                    marginBottom: 'var(--space-1)',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  {STEP_TIP[currentWizardStep.id]!.title}
                </strong>
                {STEP_TIP[currentWizardStep.id]!.body}
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Generation Queue — always visible */}
      <div style={{ marginTop: 'var(--space-10)' }}>
        <GenerationQueue />
      </div>
    </div>
  );
};

export default HomePage;
